import { createContext, useContext, useEffect, useState } from 'react'
import FileModal, { FileExportModalMode } from './file-modal/file-modal'
import { AssetableType, CustomerState, TicketFile } from 'lib/api/ticket-files/ticket-files'
import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query'
import { request, requestQuery } from 'lib/api/fetch-api'
import { toast } from 'lib/components/toast/toast'
import FileDrawer from './file-drawer'
import ConfirmationModal from '../confirmation-modal'
import IconButton from 'lib/components/buttons/icon-button'
import { CheckCircle2Icon, DownloadIcon, ExternalLinkIcon, ForwardIcon, XIcon } from 'lucide-react'
import SelectionToolbar from 'lib/components/selection-toolbar/selection-toolbar'
import Button from 'components/core/button'
import { generateAndDownloadZip } from 'lib/util/file/file'

export type OnFinish = ({ action, ticketFiles }: { action: FileAction; ticketFiles: TicketFile[] }) => void

type FilesContextType = {
  mode: FileExportModalMode | null
  setMode: (mode: FileExportModalMode | null) => void
  selectedFiles: TicketFile[]
  setSelectedFiles: (files: TicketFile[]) => void
  onFinish?: OnFinish
  addSelectedFile: (file: TicketFile) => void
  removeSelectedFile: (file: TicketFile) => void
  isSelected: (file: TicketFile) => boolean
  files: TicketFile[]
  setSelectedFile: (file: TicketFile | null) => void
  toggleApproved: (file: TicketFile) => void
  deleteTicketFile: (file: TicketFile) => void
  drawerFileQuery: UseQueryResult<TicketFile, Error>
  drawerOpen: boolean
  setDrawerFileId: (id: number | null) => void
  confirmDelete: (file: TicketFile) => void
  bulkUpdateTicketFiles: (data: { customerState: CustomerState }) => void
  bulkDuplicateTicketFiles: (data: { assetable_type: AssetableType; assetable_id: number }[]) => void
  bulkDeleteTicketFiles: () => void
}

export enum FileAction {
  DELETE = 'delete',
  UPDATE = 'update',
  NONE = null,
}

export const filesContext = createContext<FilesContextType | null>(null)

export const useFilesContext = () => {
  const context = useContext(filesContext)
  if (!context) {
    throw new Error('useFilesContext must be used within a FilesProvider')
  }
  return context
}

export const FilesProvider = ({
  children,
  onFinish,
  isCollaboratorView = false,
  defaultBehavior = true,
  toolbar = true,
}: {
  children: React.ReactNode
  onFinish?: OnFinish
  isCollaboratorView?: boolean
  defaultBehavior?: boolean
  toolbar?: boolean
}) => {
  const params = new URLSearchParams(window.location.search)
  const [mode, setMode] = useState<FileExportModalMode | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<TicketFile[]>([])
  const [selectedFile, setSelectedFile] = useState<TicketFile | null>(null)
  const [drawerFileId, setDrawerFileId] = useState<number | null>(
    params.get('fileId') ? Number(params.get('fileId')) : null,
  )
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false)

  useEffect(() => {
    const url = new URL(window.location.href)
    if (drawerFileId) {
      url.searchParams.set('fileId', drawerFileId.toString())
      window.history.pushState({}, '', url.toString())
    }
    if (!drawerFileId && url.searchParams.get('fileId')) {
      url.searchParams.delete('fileId')
      window.history.pushState({}, '', url.toString())
    }
  }, [drawerFileId])

  const drawerFileQuery = useQuery({
    queryKey: ['drawerFile', drawerFileId],
    queryFn: requestQuery({
      endpoint: 'getTicketFile',
      query: { ticket_file_id: drawerFileId },
    }),
    enabled: !!drawerFileId,
  })

  const files = [...selectedFiles, selectedFile].filter((file) => !!file)
  const isAnyConcept = files.some((file) => file?.customerState === CustomerState.Concept)

  const { mutate: updateTicketFile } = useMutation({
    mutationFn: (ticketFile: TicketFile) =>
      request({
        endpoint: 'updateTicketFile',
        query: {
          ticket_file_id: ticketFile.id,
          customer_state:
            ticketFile.customerState === CustomerState.Approved ? CustomerState.Concept : CustomerState.Approved,
        },
      }),
    onSuccess: (ticketFile) => {
      onFinish?.({ action: FileAction.UPDATE, ticketFiles: [ticketFile] })
    },
    onError: (error) => {
      toast.error('Failed to update ticket file')
      console.error(error)
    },
  })

  const { mutate: bulkUpdateTicketFiles } = useMutation({
    mutationFn: (data: { customerState: CustomerState }) =>
      request({
        endpoint: 'bulkUpdateTicketFiles',
        body: {
          ids: selectedFiles.map((file) => file.id),
          customer_state: data.customerState,
        },
      }),
    onSuccess: (data) => {
      onFinish?.({ action: FileAction.UPDATE, ticketFiles: selectedFiles })
      setSelectedFiles(data)
    },
    onError: (error) => {
      toast.error('Failed to update ticket file')
      console.error(error)
    },
  })

  const { mutate: bulkDuplicateTicketFiles } = useMutation({
    mutationFn: (data: { assetable_type: AssetableType; assetable_id: number }[]) =>
      request({
        endpoint: 'bulkDuplicateTicketFiles',
        body: {
          ids: selectedFiles.map((file) => file.id),
          data,
        },
      }),
    onSuccess: () => {
      onFinish?.({ action: FileAction.UPDATE, ticketFiles: selectedFiles })
    },
    onError: (error) => {
      toast.error('Failed to update ticket file')
      console.error(error)
    },
  })

  const { mutate: bulkCreateFileSignedUrls } = useMutation({
    mutationFn: () =>
      request({
        endpoint: 'bulkCreateFileSignedUrls',
        body: {
          ticket_file_ids: selectedFiles.map((file) => file.id),
        },
      }),
    onSuccess: (data) => {
      generateAndDownloadZip(data.urls, `download_assets_${new Date().toLocaleDateString().replaceAll('/', '')}`)
    },
    onError: (error) => {
      toast.error('Failed to download ticket files')
      console.error(error)
    },
  })

  const { mutate: bulkDeleteTicketFiles } = useMutation({
    mutationFn: () =>
      request({
        endpoint: 'bulkDeleteTicketFiles',
        body: {
          ids: selectedFiles.map((file) => file.id),
        },
      }),
    onSuccess: () => {
      onFinish?.({ action: FileAction.DELETE, ticketFiles: selectedFiles })
    },
    onError: (error) => {
      toast.error('Failed to delete ticket file')
      console.error(error)
    },
  })

  const { mutate: deleteTicketFileMutation } = useMutation({
    mutationFn: (ticketFile: TicketFile) =>
      request({
        endpoint: 'deleteTicketFile',
        query: {
          ticket_file_id: ticketFile.id,
        },
      }),
    onSuccess: (ticketFile) => {
      onFinish?.({ action: FileAction.DELETE, ticketFiles: [ticketFile] })
      setConfirmationModalVisible(false)
      setSelectedFile(null)
      toast.success('File deleted')
    },
    onError: (error) => {
      toast.error('Failed to delete ticket file')
      console.error(error)
    },
  })

  const deleteFile = (file: TicketFile) => {
    setConfirmationModalVisible(true)
    setSelectedFile(file)
  }

  const addSelectedFile = (file: TicketFile) => {
    setSelectedFiles([...selectedFiles, file])
  }

  const removeSelectedFile = (file: TicketFile) => {
    setSelectedFiles(selectedFiles.filter((f) => f.id !== file.id))
  }

  const isSelected = (ticketFile: TicketFile) => {
    return selectedFiles.some((file) => file.id === ticketFile.id)
  }

  const setSingleSelectedFile = (file: TicketFile) => {
    setSelectedFiles([])
    setSelectedFile(file)
  }

  const value = {
    mode,
    setMode,
    selectedFiles,
    setSelectedFiles,
    files,
    setSelectedFile: setSingleSelectedFile,
    onFinish,
    addSelectedFile,
    removeSelectedFile,
    isSelected,
    toggleApproved: updateTicketFile,
    deleteTicketFile: deleteFile,
    confirmDelete: deleteTicketFileMutation,
    drawerFileQuery,
    setDrawerFileId,
    drawerOpen: !!drawerFileId,
    bulkUpdateTicketFiles,
    bulkDuplicateTicketFiles,
    bulkDeleteTicketFiles,
  }

  return (
    <filesContext.Provider value={value}>
      {children}
      {defaultBehavior && !isCollaboratorView && (
        <>
          <FileDrawer />
          <ConfirmationModal
            title="Are you sure you want to delete this file?"
            message="This action cannot be undone."
            confirmBtnText="Delete"
            cancelBtnText="Cancel"
            cancelAction={() => {
              setConfirmationModalVisible(false)
              setSelectedFile(null)
            }}
            confirmAction={() => {
              deleteTicketFileMutation(selectedFile)
            }}
            visible={confirmationModalVisible}
          />
          {files.length > 0 && !isCollaboratorView && <FileModal />}
        </>
      )}
      {selectedFiles.length > 0 && toolbar && (
        <SelectionToolbar portalId="selection-toolbar-project-drawer-container">
          <div className="tw-flex tw-items-center tw-gap-4 tw-p-2">
            <div className="tw-flex tw-items-center tw-gap-1">
              <IconButton
                color="transparent"
                onClick={() => {
                  setSelectedFiles([])
                }}
                className="tw-mx-0 tw-px-0 tw-text-white hover:tw-bg-transparent hover:tw-text-neutral-100"
                size="2xs"
              >
                <XIcon className="lu-md" />
              </IconButton>
              {selectedFiles.length} selected
            </div>
            <Button
              color={isAnyConcept ? 'neutral' : 'softGreen'}
              className="tw-truncate tw-rounded-full tw-px-2 tw-py-1"
              size="cut"
              onClick={() => {
                const newState = isAnyConcept ? CustomerState.Approved : CustomerState.Concept

                bulkUpdateTicketFiles({
                  customerState: newState,
                })
              }}
            >
              <CheckCircle2Icon className="tw-mr-1 tw-h-4 tw-w-4" />
              {isAnyConcept ? 'Mark approved' : 'Approved'}
            </Button>
            <div className="tw-flex tw-items-center tw-gap-2">
              <IconButton
                color="transparent"
                onClick={() => {
                  setMode(FileExportModalMode.ADD_TO)
                }}
                className="tw-mx-0 tw-px-0 tw-text-white hover:tw-bg-transparent hover:tw-text-neutral-100"
                size="2xs"
              >
                <ForwardIcon className="lu-md" />
              </IconButton>
              <IconButton
                color="transparent"
                onClick={() => {
                  if (selectedFiles.length === 1) {
                    setMode(FileExportModalMode.DOWNLOAD)
                  } else {
                    bulkCreateFileSignedUrls()
                  }
                }}
                size="2xs"
                className="tw-mx-0 tw-px-0 tw-text-white hover:tw-bg-transparent hover:tw-text-neutral-100"
              >
                <DownloadIcon className="lu-md" />
              </IconButton>
              <IconButton
                color="transparent"
                onClick={() => {
                  setMode(FileExportModalMode.EXPORT)
                }}
                className="tw-mx-0 tw-px-0 tw-text-white hover:tw-bg-transparent hover:tw-text-neutral-100"
                size="2xs"
              >
                <ExternalLinkIcon className="lu-md" />
              </IconButton>
            </div>
          </div>
        </SelectionToolbar>
      )}
    </filesContext.Provider>
  )
}
